import React, { useState } from 'react';
import { X, Copy, Share2, Check } from 'lucide-react';

const SharePlaylistModal = ({ playlist, onClose, onToggleShare }) => {
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const shareUrl = playlist.share_token && playlist.is_public
    ? `${window.location.origin}/shared/playlist/${playlist.share_token}`
    : null;

  const handleToggleShare = async () => {
    setLoading(true);
    try {
      await onToggleShare();
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>Share Playlist</h3>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body" style={{ padding: '24px' }}>
          {/* Playlist Info */}
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ fontSize: '18px', marginBottom: '8px', color: '#fff' }}>
              {playlist.name}
            </h4>
            {playlist.description && (
              <p style={{ fontSize: '14px', color: '#999' }}>
                {playlist.description}
              </p>
            )}
            <p style={{ fontSize: '14px', color: '#999', marginTop: '8px' }}>
              {playlist.song_ids?.length || 0} songs
            </p>
          </div>

          {/* Share Toggle */}
          <div style={{
            background: 'rgba(157, 78, 221, 0.1)',
            border: '1px solid rgba(157, 78, 221, 0.3)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <Share2 size={18} color="#9D4EDD" />
                  <span style={{ fontSize: '16px', fontWeight: '600', color: '#fff' }}>
                    Public Sharing
                  </span>
                </div>
                <p style={{ fontSize: '13px', color: '#999', margin: 0 }}>
                  {playlist.is_public 
                    ? 'Anyone with the link can view this playlist'
                    : 'Only you can see this playlist'
                  }
                </p>
              </div>
              <button
                onClick={handleToggleShare}
                disabled={loading}
                style={{
                  width: '60px',
                  height: '32px',
                  borderRadius: '16px',
                  border: 'none',
                  background: playlist.is_public ? '#9D4EDD' : '#444',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  position: 'relative',
                  transition: 'background 0.3s',
                  opacity: loading ? 0.6 : 1
                }}
              >
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'white',
                  position: 'absolute',
                  top: '4px',
                  left: playlist.is_public ? '32px' : '4px',
                  transition: 'left 0.3s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }} />
              </button>
            </div>
          </div>

          {/* Share Link */}
          {playlist.is_public && shareUrl && (
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '600', 
                marginBottom: '8px',
                color: '#fff'
              }}>
                Share Link
              </label>
              <div style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center'
              }}>
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #444',
                    background: '#2a2a2a',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                  onClick={(e) => e.target.select()}
                />
                <button
                  onClick={handleCopy}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: copied ? '#10b981' : '#9D4EDD',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s',
                    minWidth: '100px',
                    justifyContent: 'center'
                  }}
                >
                  {copied ? (
                    <>
                      <Check size={16} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <p style={{ 
                fontSize: '12px', 
                color: '#999', 
                marginTop: '8px',
                fontStyle: 'italic'
              }}>
                Share this link with anyone to let them listen to your playlist
              </p>
            </div>
          )}

          {!playlist.is_public && (
            <div style={{
              padding: '16px',
              borderRadius: '8px',
              background: 'rgba(255, 255, 255, 0.05)',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '14px', color: '#999', margin: 0 }}>
                Enable public sharing to get a shareable link
              </p>
            </div>
          )}

          {playlist.is_public && !shareUrl && (
            <div style={{
              padding: '16px',
              borderRadius: '8px',
              background: 'rgba(157, 78, 221, 0.1)',
              border: '1px solid rgba(157, 78, 221, 0.3)',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '14px', color: '#9D4EDD', margin: '0 0 8px 0', fontWeight: '600' }}>
                {loading ? 'Generating share link...' : 'Generate Share Link'}
              </p>
              {!loading && (
                <p style={{ fontSize: '12px', color: '#999', margin: 0 }}>
                  Toggle sharing off and on again to generate your link
                </p>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ padding: '16px 24px', borderTop: '1px solid #333' }}>
          <button 
            className="button secondary" 
            onClick={onClose}
            style={{ width: '100%' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SharePlaylistModal;
